import { useState, useRef, useCallback } from 'react';
import update from 'immutability-helper';
import { jsPDF as JSPDF } from 'jspdf';

import XlinkAPI from '~/app/api/xlinkAPI';
import WebHelpers from '~/app/webHelpers.js';
import ErrorHelpers from '~/app/errorHelpers.js';
import { toBMPDataURL, sigTypes } from '~/app/bmpConversionHelpers.js';
import {
  PRINT_TYPES,
  PRINT_PREFS,
  PRINT_ACTION_TYPES,
  SIGNATURE_PAD_TYPES,
  SIGNEE_TYPE,
  PRINT_TYPES_MAP,
} from '~/app/constants';
import { store } from '~/app/redux/index';
import { actions as remoteSignActions } from '~/app/redux/remoteSign/duck';
import {
  fetchAndDispatchRemoteData,
  withoutSignaturesOnFile,
} from '~/app/Pages/Returns/pages/Signatures/signaturesHelper.js';

// ─── Initial state shapes ────────────────────────────────────────────────────

const INITIAL_PRINT = {
  pType: '',
  formOccur: '',
  prefs: {},
  overridePrefs: {},
  reqSigs: '',
  originalSignquery: '', // reqSigs is 'signquery' but it gets updated when signatures are captured.
  currentSig: '',
  signatures: {},
  ppSignature: '',
  efinSignature: '',
  ppxSignature: '',
  pAction: '',
  pActionEligible: '',
  val: '',
  menuText: '',
  cltr: 'English', // Default client letter to english
  consent: '',
};

const INITIAL_REMOTE_SIGNATURE = {
  returnCurrentState: '',
  remoteSignReturnID: '',
  tpFirstName: '',
  tpLastName: '',
  tpDOB: '',
  tpLast4: '',
  tpEmail: '',
  tpCellPhone: '',
  tpCellCarrier: '',
  tpCellDomain: '',
  spFirstName: '',
  spLastName: '',
  spDOB: '',
  spLast4: '',
  spEmail: '',
  spCellPhone: '',
  spCellCarrier: '',
  spCellDomain: '',
};

/**
 * usePrint
 *
 * Owns the print / signature subsystem extracted from FormViewer.
 *
 * State owned:
 *   print              – composite print config object (pType, formOccur, prefs, …)
 *   printStateAcksModal
 *   stateAcksPType     – pType the state ACK modal will print (state / amended / extension)
 *   printStateClientLettersModal
 *   printComponentsModal
 *   noPrintDemoModal
 *   openPrintDemoMessage
 *   showFinalPDF
 *   returnPrintQueryLoading
 *   encryptedPdfPrint
 *   remoteSignature    – composite remote-sign object
 *   remoteSignPrint
 *   btnCompletedRemoteSignClose
 *   availablePackages
 *   sigPType           – top-level pType; set in handleSigningInOffice
 *
 * Instance vars (useRef):
 *   sigDocIDRef        – signature document ID
 *
 * Inputs: see parameter list below.
 */
const usePrint = ({
  calcAPIRef,
  formNavRef,
  printCallbacksRef,
  // Redux props / actions
  returnID,
  isReadOnly,
  usingDemoAccount,
  isRMSSigningMethod,
  preferredSignaturePad,
  isSignatureRequestActive,
  formList,
  signatureRequestActiveSet,
  showSnackbarMessage,
  showErrorDialog,
  toggleSignatureCaptureModal,
  textLinkModalContentOriginal,
  toggleTextLinkSendModal,
  setPreferredSignaturePad,
  payload,
}) => {
  // ── State ──────────────────────────────────────────────────────────────────

  const [print, setPrint] = useState(INITIAL_PRINT);
  const [printStateAcksModal, setPrintStateAcksModal] = useState(false);
  const [stateAcksPType, setStateAcksPType] = useState(PRINT_TYPES.STATE_ACK_LETTER);
  const [printStateClientLettersModal, setPrintStateClientLettersModal] = useState(false);
  const [printComponentsModal, setPrintComponentsModal] = useState(false);
  const [noPrintDemoModal, setNoPrintDemoModal] = useState(false);
  const [openPrintDemoMessage, setOpenPrintDemoMessage] = useState(false);
  const [showFinalPDF, setShowFinalPDF] = useState(true);
  const [returnPrintQueryLoading, setReturnPrintQueryLoading] = useState(false);
  const [encryptedPdfPrint, setEncryptedPdfPrint] = useState(false);
  const [remoteSignature, setRemoteSignature] = useState(INITIAL_REMOTE_SIGNATURE);
  const [remoteSignPrint, setRemoteSignPrint] = useState(false);
  const [pendingSignatureModal, setPendingSignatureModal] = useState(false);
  const [btnCompletedRemoteSignClose, setBtnCompletedRemoteSignClose] = useState(false);
  const [availablePackages, setAvailablePackages] = useState([]);
  const [sigPType, setSigPType] = useState('');

  // ── Instance vars ──────────────────────────────────────────────────────────

  const sigDocIDRef = useRef(0);
  const handleSignatureCaptureRef = useRef(null);
  const internalPrintRef = useRef(print);
  internalPrintRef.current = print;

  // ── Utility helpers (pure, no state) ──────────────────────────────────────

  /**
   * Draws a base64-encoded image onto a canvas context.
   * Returns a promise that resolves when the image has been drawn.
   */
  const saveCanvasImage = (data, ctx) =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(ctx.drawImage(image, 0, 0));
      image.onerror = reject;
      image.src = data;
    });

  /**
   * Converts a base64 PDF string to a Blob of the given content type.
   */
  const convertToBlob = (pdfString, contentType) => {
    const byteChar = atob(pdfString);
    const byteNum = new Array(byteChar.length);
    for (let i = 0; i < byteChar.length; i++) {
      byteNum[i] = byteChar.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNum);
    return new Blob([byteArray], { type: contentType });
  };

  /**
   * Recursively appends child form entries to the jsPDF document.
   * Returns the updated currentRow after all entries are appended.
   */
  const generateAttachedWorksheets = (doc, wks, currentRow) => {
    wks.forEach(form => {
      currentRow = currentRow + 8;
      if (currentRow > 280) {
        doc.addPage();
        doc.text('Attached Forms List', 15, 20);
        currentRow = 30;
      }
      doc.text(form.desc, 25, currentRow);
    });
    return currentRow;
  };

  /**
   * Appends form entries (and their worksheets) to the jsPDF document.
   * Returns the updated currentRow after all entries are appended.
   */
  const generateAttachedForms = (doc, forms, currentRow) => {
    currentRow = currentRow + 8;
    forms.forEach(form => {
      if (currentRow > 280) {
        doc.addPage();
        doc.text('Attached Forms List', 15, 20);
        currentRow = 30;
      }
      doc.text(form.desc, 20, currentRow);
      if (form.GChildren !== undefined) {
        currentRow = generateAttachedWorksheets(doc, form.GChildren, currentRow);
      }
      currentRow = currentRow + 8;
    });
    return currentRow;
  };

  /**
   * Maps the current print preferences to the flat object that the calc server
   * expects for a given print type.
   */
  const convertPrintPrefs = (prefs, printType) => {
    const newPrefsToSend = {};
    switch (printType) {
      case PRINT_TYPES.PREVIEW_RETURN:
      case PRINT_TYPES.FINAL_RETURN:
        PRINT_PREFS.FINAL.forEach(pref => {
          if (
            Object.prototype.hasOwnProperty.call(prefs, pref) &&
            (payload?.is_business ? PRINT_PREFS.CORP_FINAL : PRINT_PREFS.FINAL).includes(pref)
          ) {
            newPrefsToSend[pref] = prefs[pref]?.configValue === 1 ? 'X' : '';
          }
        });
        break;
      case PRINT_TYPES.BANK_DOCUMENTS:
        newPrefsToSend[PRINT_PREFS.BANK] = prefs?.[PRINT_PREFS.BANK]?.configValue === 1 ? 'X' : '';
        break;
      case PRINT_TYPES.DISCLOSURE:
        newPrefsToSend[PRINT_PREFS.DISCLOSURE] =
          prefs?.[PRINT_PREFS.DISCLOSURE]?.configValue === '1' ? 'X' : '';
        break;
      case PRINT_TYPES.ORGANIZER:
        break;
      case PRINT_TYPES.EF_DOCUMENTS:
        for (const pref in PRINT_PREFS.EF) {
          const key = PRINT_PREFS.EF[pref];
          newPrefsToSend[key] = prefs?.[key]?.configValue === 1 ? 'X' : '';
        }
        break;
      case PRINT_TYPES.AMENDED_RETURN:
      case PRINT_TYPES.CURRENT_FORM:
      case PRINT_TYPES.REF_COUPONS:
      case PRINT_TYPES.ACK_LETTER:
      case PRINT_TYPES.STATE_ACK_LETTER:
      case PRINT_TYPES.AMENDED_ACK_LETTER:
      case PRINT_TYPES.CLIENT_LETTER:
      default:
        break;
    }
    return newPrefsToSend;
  };

  // ── Package availability ───────────────────────────────────────────────────

  /**
   * Filters a package data array down to only the packages present in
   * the availablePackages state list.
   */
  const sortAvailablePackages = useCallback(
    packageData => {
      const sortedData = [];
      for (let i = 0; i < packageData.length; i++) {
        if (availablePackages.includes(packageData[i].ListItem[0].COL)) {
          sortedData.push(packageData[i]);
        }
      }
      return sortedData;
    },
    [availablePackages],
  );

  /**
   * Fetches the list of available packages from the server and stores them
   * in state (sorted).
   */
  const getAvailablePackages = async () => {
    const res = await XlinkAPI.getStatePackages(payload?.is_business);
    setAvailablePackages(res.data?.sort() || []);
  };

  /**
   * Returns true if the given package string's base portion is in the
   * availablePackages list.
   */
  const isPackageAvailable = useCallback(
    packageString => {
      const statePackage = packageString.replace(/(.*)-.*/, '$1');
      return availablePackages.includes(statePackage);
    },
    [availablePackages],
  );

  // ── Print preferences ──────────────────────────────────────────────────────

  /**
   * Loads the office's printing setup from the server and merges the result
   * into print.prefs.
   */
  const getPrintingPrefs = () => {
    XlinkAPI.getPrintingSetup(WebHelpers.getJWTPayload().login_id)
      .then(res => {
        const data = res.data;
        const newPrefs = update(print, {
          prefs: { $set: data.config },
        });
        setPrint(newPrefs);
      })
      .catch(err => {
        console.log(err);
      });
  };

  // ── Display / preview ──────────────────────────────────────────────────────

  /**
   * Opens the PDF preview overlay with the provided base64 PDF blob.
   */
  const displayPrint = pdfBlob => {
    printCallbacksRef?.current?.onShowPreview?.(`data:application/pdf;base64,${pdfBlob}`);
  };

  // ── Print workflow ─────────────────────────────────────────────────────────

  /**
   * Sends the ReturnPrint command to the calc server.
   * Starts the loading spinner unless an RMS signing method is active.
   */
  const printReturn = (
    type,
    prefs,
    formOccur,
    sigs,
    pAction,
    originalSignquery = '',
    consent = '',
  ) => {
    if (!isRMSSigningMethod) {
      printCallbacksRef?.current?.onStartSpin?.('Loading PDF');
    }
    calcAPIRef.current.ReturnPrint(
      type,
      prefs,
      formOccur,
      sigs,
      pAction,
      '',
      print.cltr,
      isReadOnly,
      '',
      originalSignquery,
      consent,
    );
  };

  /**
   * Queries the calc server for the required signatures for the given form
   * and print type.  Sets returnPrintQueryLoading and starts the spinner.
   */
  const handleGetReqSigs = useCallback(
    (formOccur, type) => {
      const prefs = print.prefs;
      let LPA5 = '';
      let LPB5 = '';
      if (prefs.LPA5?.configValue !== undefined) {
        LPA5 = prefs.LPA5.configValue === '1' ? 'X' : '';
      }
      if (prefs.LPB5?.configValue !== undefined) {
        LPB5 = prefs.LPB5.configValue === '1' ? 'X' : '';
      }
      calcAPIRef.current.ReturnPrintSignatures(formOccur, type, LPA5, LPB5, isReadOnly);
      setReturnPrintQueryLoading(true);
      printCallbacksRef?.current?.onStartSpin?.('Loading');
    },
    [print.prefs, calcAPIRef, isReadOnly, printCallbacksRef],
  );

  /**
   * Updates the print state with the new type, formOccur, and overrides, then
   * queries the required signatures.  Falls back to the demo guard if the
   * account is a demo account.
   */
  const handlePrint = useCallback(
    (type, prefs, val, menuText = '') => {
      if (!usingDemoAccount) {
        // These print types carry the user's selection (state code / letter name)
        // in `val` instead of using the form that is currently open.
        const usesSelectedValue = [
          PRINT_TYPES.STATE_ACK_LETTER,
          PRINT_TYPES.CLIENT_LETTER,
          PRINT_TYPES.STATE_AMENDED_ACK_LETTER,
          PRINT_TYPES.STATE_EXT_ACK_LETTER,
        ].includes(type);
        let formOccur = usesSelectedValue ? val : formNavRef?.current?.activeForm;
        const value = usesSelectedValue ? val : formNavRef?.current?.activeForm;
        if (type === PRINT_TYPES.CLIENT_LETTER && val !== 'Federal' && val !== 'Engagement')
          formOccur = 'State';
        const printState = update(print, {
          pType: { $set: type },
          formOccur: { $set: formOccur },
          overridePrefs: { $set: prefs },
          val: { $set: value },
          menuText: { $set: menuText },
        });
        setPrint(printState);
        handleGetReqSigs(formOccur, type);
      } else {
        handleNoPrintDemo();
      }
    },
    [usingDemoAccount, formNavRef, print, handleGetReqSigs],
  );

  /**
   * Sets the preferred signature pad to ON_SCREEN.
   */
  const handlePreferredSignaturePad = () => {
    let signaturePadType = SIGNATURE_PAD_TYPES.INITIAL_VALUE;
    signaturePadType = SIGNATURE_PAD_TYPES.ON_SCREEN;
    setPreferredSignaturePad(signaturePadType);
  };

  /**
   * Activates the remote-sign flow.
   *
   * Called from two paths:
   * 1. PrintActionsDialog (no args) — user clicked "Remote Sign". Saves if
   *    needed, sets initial signee data, and opens the SendSignature modal.
   * 2. useSpecialFormLoad (with formName) — signature form is loading.
   *    Only sets the preferred pad.
   */
  const handleRemoteSignSelected = useCallback(
    async formName => {
      if (!formName) {
        signatureRequestActiveSet(true);
      }
      handlePreferredSignaturePad();

      if (formName) return;

      const documentName = PRINT_TYPES_MAP[print.pType] || '';

      // Await the ERO/preparer fetch before picking the first signee.
      // withoutSignaturesOnFile reads amendedPreparerData, which this fetch
      // populates; selecting beforehand would read the empty initial value and
      // prompt for an amended preparer who already has a signature on file.
      const preFetchState = store.getState();
      await fetchAndDispatchRemoteData(
        print.efinSignature,
        preFetchState.formViewer.preparerID,
        preFetchState.formViewer.amendedPreparerID,
        preFetchState.returnProfile?.returnID,
      );

      const remoteSignState = store.getState();
      const signaturesNeeded = withoutSignaturesOnFile(
        remoteSignState.remoteSign.signaturesNeeded,
        remoteSignState.remoteSign.amendedPreparerData,
      );
      const firstSignee = Object.keys(signaturesNeeded).find(key => signaturesNeeded[key]);

      if (!firstSignee) {
        signatureRequestActiveSet(false);
        return;
      }

      store.dispatch(
        remoteSignActions.initialSigneeDataSet({
          signee: firstSignee,
          document: documentName,
          signatureDocID: 0,
        }),
      );

      // Always save so any unsaved contact info (e.g. a newly entered email)
      // is flushed to Redux before the modal opens. The return_save handler
      // in useSocketMessage will open the modal once the save response arrives.
      setPendingSignatureModal(true);
      printCallbacksRef?.current?.onStartSpin?.('Saving return data...');
      printCallbacksRef?.current?.onSaveReturn?.();
    },
    [signatureRequestActiveSet, print.pType, print.efinSignature, printCallbacksRef],
  );

  /**
   * Executes the print action (pAction = '1') for the currently queued print
   * job.  Routes state client letters through ReturnPrintStateClientLetter.
   */
  const handlePrintActionsPrintSelected = useCallback(() => {
    const type = print.pType;
    const prefs =
      type === PRINT_TYPES.RETURN_COMPONENTS
        ? print.overridePrefs
        : convertPrintPrefs(print.prefs, type);
    const formOccur = print.formOccur;
    const pAction = '1';
    if (formOccur !== 'State') {
      calcAPIRef.current.ReturnPrint(
        type,
        prefs,
        formOccur,
        {},
        pAction,
        '',
        print.cltr,
        isReadOnly,
      );
    } else {
      calcAPIRef.current.ReturnPrintStateClientLetter(
        type,
        prefs,
        formOccur,
        {},
        pAction,
        '',
        print.cltr,
        print.val,
        isReadOnly,
      );
    }
    printCallbacksRef?.current?.onStartSpin?.('Loading PDF');
  }, [print, calcAPIRef, isReadOnly, printCallbacksRef]);

  // ── Signing ────────────────────────────────────────────────────────────────

  /**
   * Opens the signature pad for an in-office signing session.
   * Stores the sigDocID and routes to the correct signature type.
   */
  const openSignaturePad = useCallback(
    (reqSigs, type, padType = preferredSignaturePad) => {
      const ppSignature = print.ppSignature;
      const amendedPreparerSignature = print.ppxSignature;
      const efinSignature = print.efinSignature;
      const canvas = printCallbacksRef?.current?.onGetCanvas?.();
      const ctx = canvas?.getContext('2d');
      const curSig = reqSigs.charAt(0);
      const newReqSigs = reqSigs.slice(1);
      const printState = update(print, {
        pType: { $set: type },
        currentSig: { $set: curSig },
        reqSigs: { $set: newReqSigs },
      });
      setPrint(printState);
      if (curSig === SIGNEE_TYPE.PREPARER && ppSignature.length !== 0) {
        getPreparerSignature(ppSignature, ctx, canvas);
      } else if (
        curSig === SIGNEE_TYPE.AMENDED_PAID_PREPARER_SIGNATURE &&
        amendedPreparerSignature.length !== 0
      ) {
        // The print query reports the amended preparer as missing for everyone,
        // because return_print_query carries no signature arguments. Fill it from
        // the saved signature the same way the preparer and ERO are handled here
        // (and as setRequiredSignaturesNew already does), otherwise the pad opens
        // and the user is asked to re-sign a signature that is already on file.
        getPreparerSignature(amendedPreparerSignature, ctx, canvas);
      } else if (curSig === SIGNEE_TYPE.ERO && efinSignature.length !== 0) {
        getEROSignature(efinSignature, ctx, canvas);
      } else {
        if (padType === SIGNATURE_PAD_TYPES.ON_SCREEN) {
          toggleSignatureCaptureModal(true);
        }
      }
    },
    [print, printCallbacksRef, preferredSignaturePad, toggleSignatureCaptureModal],
  );

  /**
   * Handles in-office signing.  Reads the stored reqSigs and routes to
   * openSignaturePad with either the remote or local sigDocID.
   */
  const handleSigningInOffice = useCallback(
    async (sigDocID = 0, remoteRequestSignatures = '', docType = '') => {
      handlePreferredSignaturePad();
      const padType = SIGNATURE_PAD_TYPES.ON_SCREEN;
      const reqSigs = print.reqSigs;
      const type = Object.keys(PRINT_TYPES_MAP)[Object.values(PRINT_TYPES_MAP).indexOf(docType)];
      setSigPType(type);
      if (!remoteRequestSignatures.length && reqSigs.length !== 0 && docType !== '') {
        sigDocIDRef.current = parseInt(sigDocID);
        openSignaturePad(reqSigs, type, padType);
      } else if (remoteRequestSignatures.length) {
        sigDocIDRef.current = parseInt(sigDocID);
        openSignaturePad(remoteRequestSignatures, type, padType);
      } else if (!remoteRequestSignatures.length && docType === '') {
        // Print-menu Sign path (no explicit signature document). Reset the ref:
        // a stale sigDocID from an earlier RMS capture flow would otherwise route
        // these captures into that previous request's signature document,
        // completing the wrong RMS instead of signing the current print job.
        sigDocIDRef.current = 0;
        openSignaturePad(reqSigs, print.pType, padType);
      }
    },
    [print.reqSigs, print.pType, openSignaturePad],
  );

  /**
   * Initiates a remote signing request via ReturnPrint with the
   * REMOTE_SIGN_ACTION pAction.
   */
  const handleSigningRemotely = useCallback(
    (docType = '') => {
      setRemoteSignPrint(false);
      setShowFinalPDF(false);
      printCallbacksRef?.current?.onStartSpin?.('Sending Request...');
      const type = Object.keys(PRINT_TYPES_MAP)[Object.values(PRINT_TYPES_MAP).indexOf(docType)];
      const prefs = print.prefs;
      const formOccur = print.formOccur;
      const pAction = PRINT_ACTION_TYPES.REMOTE_SIGN_ACTION;
      calcAPIRef.current.ReturnPrint(
        type,
        prefs,
        formOccur,
        {},
        pAction,
        '',
        print.cltr,
        isReadOnly,
        '',
        print.originalSignquery,
        print.consent,
      );
    },
    [print, calcAPIRef, isReadOnly, printCallbacksRef],
  );

  // ── Signature capture / canvas ─────────────────────────────────────────────

  /**
   * Fetches the ERO signature from the server and either draws it on the
   * canvas (via convertImage) or opens the signature pad.
   */
  const getEROSignature = useCallback(
    (efinSignature, ctx, canvas) => {
      XlinkAPI.getefinSignature(efinSignature)
        .then(res => {
          const data = res.data;
          if (
            data.ero_signature === null ||
            data.ero_signature === '' ||
            data.ero_signature === undefined
          ) {
            if (preferredSignaturePad === SIGNATURE_PAD_TYPES.ON_SCREEN) {
              toggleSignatureCaptureModal(true);
            }
          } else {
            convertImage(data.ero_signature, ctx, canvas, data.ero_device_used);
          }
        })
        .catch(error => {
          ErrorHelpers.handleError('Unable to Fetch ERO Signature Data', error);
        });
    },
    [returnID, preferredSignaturePad, toggleSignatureCaptureModal],
  );

  /**
   * Fetches the preparer signature from the server and either draws it on the
   * canvas (via convertImage) or opens the signature pad.
   */
  const getPreparerSignature = useCallback(
    (ppSignature, ctx, canvas) => {
      XlinkAPI.getppSignature(ppSignature, returnID)
        .then(res => {
          const data = res.data;
          if (
            data.preparer_signature === null ||
            data.preparer_signature === '' ||
            data.preparer_signature === undefined
          ) {
            if (preferredSignaturePad === SIGNATURE_PAD_TYPES.ON_SCREEN) {
              toggleSignatureCaptureModal(true);
            }
          } else {
            convertImage(data.preparer_signature, ctx, canvas, data.preparer_device_used);
          }
        })
        .catch(error => {
          ErrorHelpers.handleError('Unable to Fetch Preparer Signature Data', error);
        });
    },
    [returnID, preferredSignaturePad, toggleSignatureCaptureModal],
  );

  /**
   * Converts a raw signature image to a BMP data URL and then calls
   * handleSignatureCapture with the result.
   */
  const convertImage = useCallback(
    async (data, ctx, canvas, signaturePadType) => {
      await saveCanvasImage(data, ctx);
      const padType = 'screen';
      let sigString = '';
      try {
        sigString = await toBMPDataURL(canvas, padType, signaturePadType, 'convertImage');
      } catch (error) {
        console.log('toBMPDataURL - convertImage - error: ', error);
        ErrorHelpers.handleError('Error formatting signature 2', error);
      }
      sigString = sigString.replace('data:image/bmp;base64,', '');
      handleSignatureCaptureRef.current(
        sigTypes[internalPrintRef.current.currentSig],
        sigString,
        preferredSignaturePad,
      );
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    },
    [preferredSignaturePad],
  );

  /**
   * Advances to the next required signature or submits all collected
   * signatures to the server.
   */
  const setRequiredSignaturesNew = useCallback(
    reqSigs => {
      const ppSignature = print.ppSignature;
      const amendedPreparerSignature = print.ppxSignature;
      const efinSignature = print.efinSignature;
      const canvas = printCallbacksRef?.current?.onGetCanvas?.();
      const ctx = canvas?.getContext('2d');
      const curSig = reqSigs.charAt(0);
      const newReqSigs = reqSigs.slice(1);
      const printState = update(print, {
        currentSig: { $set: curSig },
        reqSigs: { $set: newReqSigs },
      });
      setPrint(printState);
      if (curSig === '3' && ppSignature.length !== 0) {
        getPreparerSignature(ppSignature, ctx, canvas);
      } else if (curSig === 'A' && amendedPreparerSignature.length !== 0) {
        getPreparerSignature(amendedPreparerSignature, ctx, canvas);
      } else if (curSig === '4' && efinSignature.length !== 0) {
        getEROSignature(efinSignature, ctx, canvas);
      } else {
        toggleSignatureCaptureModal(true);
      }
    },
    [print, printCallbacksRef, getPreparerSignature, getEROSignature, toggleSignatureCaptureModal],
  );

  /**
   * Captures a single signature value, persists it to the server if a
   * sigDocID is present, and either advances to the next required signature
   * or finalises the signing flow.
   */
  const handleSignatureCapture = useCallback(
    async (sigType, sigValue, signaturePadType) => {
      if (signaturePadType === SIGNATURE_PAD_TYPES.ON_SCREEN) {
        toggleSignatureCaptureModal(false);
      }
      const signatures = print.signatures;
      signatures[sigType] = sigValue;
      const printState = update(print, { signatures: { $set: signatures } });
      setPrint(printState);
      const reqSigs = print.reqSigs;
      try {
        if (sigDocIDRef.current !== 0) {
          const res = await XlinkAPI.postInOfficeSignatures(
            sigDocIDRef.current,
            sigValue,
            print.currentSig.toString(),
            returnID,
          );
          if (res.status === 200 && print.reqSigs.length === 0) {
            showSnackbarMessage('Signature(s) have been successfully saved.', 'success', 3500, {
              vertical: 'top',
              horizontal: 'center',
            });
            printReturn(
              isSignatureRequestActive ? sigPType : print.pType,
              print.overridePrefs,
              print.formOccur,
              print.signatures,
              '2',
              print.originalSignquery,
              print.consent,
            );
          }
        }
      } catch (err) {
        showErrorDialog('Failed to Post In-Office Signature for Remote Signature Request');
      }
      if (print.reqSigs.length !== 0) {
        setRequiredSignaturesNew(reqSigs);
      } else if (sigDocIDRef.current === 0) {
        const res = await XlinkAPI.invalidateRemoteSignature(returnID);
        if (res.status === 200 && print.reqSigs.length === 0) {
          showSnackbarMessage('Signature(s) have been successfully saved.', 'success', 3500, {
            vertical: 'top',
            horizontal: 'center',
          });
          signatureRequestActiveSet(false);
          printReturn(
            isSignatureRequestActive ? sigPType : print.pType,
            print.overridePrefs,
            print.formOccur,
            print.signatures,
            '2',
            print.originalSignquery,
            print.consent,
          );
        }
      }
    },
    [
      print,
      toggleSignatureCaptureModal,
      returnID,
      showSnackbarMessage,
      showErrorDialog,
      signatureRequestActiveSet,
      isSignatureRequestActive,
      sigPType,
      isReadOnly,
      setRequiredSignaturesNew,
    ],
  );
  handleSignatureCaptureRef.current = handleSignatureCapture;

  // ── Print modal management ─────────────────────────────────────────────────

  /**
   * Opens the print components modal.  Shows the demo guard if the account
   * is a demo account.
   */
  const openPrintComponentsModal = () => {
    if (!usingDemoAccount) {
      setPrintComponentsModal(true);
    } else {
      handleNoPrintDemo();
    }
  };

  /**
   * Opens the "no print on demo account" modal.
   */
  const handleNoPrintDemo = () => {
    setNoPrintDemoModal(true);
  };

  /**
   * Closes the print components modal.
   */
  const closePrintComponentsModal = () => {
    setPrintComponentsModal(false);
  };

  /**
   * Requests the state ACK list from the calc server.
   */
  const getStateAckList = () => {
    calcAPIRef.current.ReturnStateAck();
    printCallbacksRef?.current?.onStartSpin?.('Checking State ACK Status...');
  };

  /**
   * Requests the amended state ACK list from the calc server.
   */
  const getAmendedStateAckList = () => {
    calcAPIRef.current.ReturnAmendedStateAck();
    printCallbacksRef?.current?.onStartSpin?.('Checking Amended State ACK Status...');
  };

  /**
   * Requests the extension state ACK list from the calc server.
   */
  const getExtensionStateAckList = () => {
    calcAPIRef.current.ReturnExtensionStateAck();
    printCallbacksRef?.current?.onStartSpin?.('Checking State Extension ACK Status...');
  };

  /**
   * Opens the state ACKs modal for the given print type (state, state amended
   * or state extension ACK letter).
   */
  const openPrintStateAcksModal = pType => {
    setPrintStateAcksModal(true);
    setStateAcksPType(pType);
  };

  /**
   * Closes the state ACKs modal and resets the print type back to the default.
   */
  const closePrintStateAcksModal = () => {
    setPrintStateAcksModal(false);
    setStateAcksPType(PRINT_TYPES.STATE_ACK_LETTER);
  };

  /**
   * Requests the list of attached states from the calc server.
   */
  const getAttachedStateList = () => {
    calcAPIRef.current.ReturnAttachedStates();
    printCallbacksRef?.current?.onStartSpin?.('Confirming Attached States...');
  };

  /**
   * Opens the state client letters modal.
   */
  const openPrintStateClientLetters = () => {
    setPrintStateClientLettersModal(true);
  };

  /**
   * Closes the state client letters modal.
   */
  const closePrintStateClientLetters = () => {
    setPrintStateClientLettersModal(false);
  };

  /**
   * Generates an attached-forms PDF using jsPDF and opens it in the preview
   * overlay.  Shows the demo guard if the account is a demo account.
   */
  const handleAttachedFormsPrint = () => {
    if (!usingDemoAccount) {
      const doc = new JSPDF();
      doc.text('Attached Forms List', 15, 20);
      let currentRow = 30;
      formList.forEach(category => {
        if (currentRow > 280) {
          doc.addPage();
          doc.text('Attached Forms List', 15, 20);
          currentRow = 30;
        }
        doc.text(category.desc, 15, currentRow);
        if (category.Children !== undefined) {
          currentRow = generateAttachedForms(doc, category.Children, currentRow);
        }
        currentRow = currentRow + 8;
      });
      const pdfURI = doc.output('datauristring');
      const pdfString = pdfURI.replace('data:application/pdf;filename=generated.pdf;base64,', '');
      displayPrint(pdfString);
    } else {
      handleNoPrintDemo();
    }
  };

  // ── TextLink ───────────────────────────────────────────────────────────────

  /**
   * Opens the TextLink send modal, fetching the message content first if it
   * has not yet been loaded.
   */
  const handleOpenTextLinkSendModal = () => {
    if (textLinkModalContentOriginal === null) {
      printCallbacksRef?.current?.onStartSpin?.('Loading...');
      calcAPIRef.current.GetTextlinkMessageContent(print.cltr);
    } else {
      toggleTextLinkSendModal(true, '');
    }
  };

  // ── State updaters for handleSocketMessage ─────────────────────────────────

  /**
   * Merges an immutability-helper update spec into the print state object.
   */
  const mergePrint = spec => {
    setPrint(prev => update(prev, spec));
  };

  /**
   * Merges an immutability-helper update spec into the remoteSignature state
   * object.
   */
  const mergeRemoteSignature = spec => {
    setRemoteSignature(prev => update(prev, spec));
  };

  /**
   * Shortcut for updating only print.cltr.
   */
  const updatePrintCltr = value => {
    setPrint(prev => update(prev, { cltr: { $set: value } }));
  };

  // ── Return ─────────────────────────────────────────────────────────────────

  return {
    // ── State ────────────────────────────────────────────────────────────────
    print,
    printStateAcksModal,
    stateAcksPType,
    printStateClientLettersModal,
    printComponentsModal,
    noPrintDemoModal,
    openPrintDemoMessage,
    showFinalPDF,
    returnPrintQueryLoading,
    encryptedPdfPrint,
    remoteSignature,
    remoteSignPrint,
    pendingSignatureModal,
    btnCompletedRemoteSignClose,
    availablePackages,
    sigPType,

    // ── State setters (used by FormViewer's handleSocketMessage and class body) ─
    setPrint,
    setPrintStateAcksModal,
    setStateAcksPType,
    setPrintStateClientLettersModal,
    setPrintComponentsModal,
    setNoPrintDemoModal,
    setOpenPrintDemoMessage,
    setShowFinalPDF,
    setReturnPrintQueryLoading,
    setEncryptedPdfPrint,
    setRemoteSignature,
    setRemoteSignPrint,
    setPendingSignatureModal,
    setBtnCompletedRemoteSignClose,
    setAvailablePackages,
    setSigPType,

    // ── Merge/update helpers ─────────────────────────────────────────────────
    mergePrint,
    mergeRemoteSignature,
    updatePrintCltr,

    // ── Utility ──────────────────────────────────────────────────────────────
    saveCanvasImage,
    convertToBlob,
    generateAttachedForms,
    generateAttachedWorksheets,
    convertPrintPrefs,

    // ── Package availability ─────────────────────────────────────────────────
    sortAvailablePackages,
    getAvailablePackages,
    isPackageAvailable,

    // ── Print preferences ────────────────────────────────────────────────────
    getPrintingPrefs,

    // ── Print workflow ───────────────────────────────────────────────────────
    printReturn,
    handlePrint,
    handleGetReqSigs,
    handlePreferredSignaturePad,
    handleRemoteSignSelected,
    handlePrintActionsPrintSelected,

    // ── Signing ──────────────────────────────────────────────────────────────
    handleSigningInOffice,
    handleSigningRemotely,
    setRequiredSignaturesNew,
    openSignaturePad,

    // ── Signature capture / canvas ───────────────────────────────────────────
    getEROSignature,
    getPreparerSignature,
    convertImage,
    handleSignatureCapture,

    // ── Display / preview ────────────────────────────────────────────────────
    displayPrint,

    // ── Print modal management ───────────────────────────────────────────────
    openPrintComponentsModal,
    handleNoPrintDemo,
    closePrintComponentsModal,
    getStateAckList,
    getAmendedStateAckList,
    getExtensionStateAckList,
    openPrintStateAcksModal,
    closePrintStateAcksModal,
    getAttachedStateList,
    openPrintStateClientLetters,
    closePrintStateClientLetters,
    handleAttachedFormsPrint,

    // ── TextLink ─────────────────────────────────────────────────────────────
    handleOpenTextLinkSendModal,
  };
};

export default usePrint;
